// src/utils/managedFieldInstances.js
// -----------------------------------------------------------------------------
// Bridge layer between organization-managed field definitions and form-local
// field instances.
//
// PURPOSE
//  • Convert an org-managed field definition into a safe form-local field instance
//  • Preserve the current form-builder contract:
//      - new local fieldId per dropped instance
//      - new local ufi per dropped instance
//  • Preserve the org-managed reference contract:
//      - stable managedFieldId
//      - stable managed option-set identity
//  • Reuse the existing getDefaultFieldMetadata(...) helper so we stay aligned
//    with the current form builder, field renderer, and save/publish pipeline
//  • Keep governance / ownership logic OUT of UI components
//
// IMPORTANT PRINCIPLES
//  • A managed field inserted into a form is still a normal form field instance
//  • The organization owns the field definition
//  • The form owns the field instance
//  • The field instance carries references back to the org-managed definition
//  • This file is additive and does not mutate existing contexts or builder flows
//
// NOTES
//  • This file intentionally does NOT read from Firestore directly.
//    It accepts already-fetched managed field definitions and produces local
//    instance metadata + layout-safe payloads.
//  • Callers (later: FormLayoutContext / builder insertion flow) should fetch
//    the managed field definition first, then call these helpers.
//
// ALIGNMENT NOTES
//  • Your current runtime expects dropdown field metadata to have a very
//    specific shape (optionsSetUFI, options, dynamicOptions, multiselect,
//    includeOther, other* text fields, dependencies.triggerOptions, etc.).
//  • getDefaultFieldMetadata(...) gives us a strong baseline, BUT for managed
//    dropdowns we must override the generic local defaults so we do NOT
//    accidentally seed "Option 1 / 2 / 3" into org-managed fields.
// -----------------------------------------------------------------------------

import { getDefaultFieldMetadata } from './firestore';

const { v4: uuidv4 } = require('uuid');

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

export const MANAGED_FIELD_INSTANCE_SOURCE = Object.freeze({
  ORGANIZATION: 'organization',
});

export const MANAGED_FIELD_INSTANCE_SCOPE = Object.freeze({
  FORM_INSTANCE: 'form-instance',
});

// -----------------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------------

const normalizeString = (value, fallback = '') => {
  return typeof value === 'string' ? value.trim() : fallback;
};

const normalizeNullableString = (value) => {
  const s = normalizeString(value, '');
  return s.length > 0 ? s : null;
};

const normalizeBoolean = (value, fallback = false) => {
  return typeof value === 'boolean' ? value : fallback;
};

const isObject = (value) => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

const assertManagedFieldDefinition = (managedFieldDefinition) => {
  if (!managedFieldDefinition || typeof managedFieldDefinition !== 'object') {
    throw new Error(
      '[managedFieldInstances] managedFieldDefinition is required.'
    );
  }

  const managedFieldId = normalizeString(managedFieldDefinition?.id);
  const type = normalizeString(managedFieldDefinition?.type);

  if (!managedFieldId) {
    throw new Error(
      '[managedFieldInstances] Managed field definition must include a stable id.'
    );
  }

  if (!type) {
    throw new Error(
      '[managedFieldInstances] Managed field definition must include a type.'
    );
  }

  if (
    type !== 'single-select' &&
    type !== 'multi-select' &&
    type !== 'asset-type'
  ) {
    throw new Error(
      `[managedFieldInstances] Unsupported managed field type for instance creation: ${type}`
    );
  }
};

const normalizeGovernance = (governance = {}) => ({
  allowLocalLabelOverride:
    governance?.allowLocalLabelOverride === true,
  allowLocalRequiredOverride:
    governance?.allowLocalRequiredOverride === true,
  allowLocalHelperTextOverride:
    governance?.allowLocalHelperTextOverride === true,
  allowLocalPlaceholderOverride:
    governance?.allowLocalPlaceholderOverride === true,
  allowLocalOptionEditing:
    governance?.allowLocalOptionEditing === true,
  allowLocalDependencies:
    governance?.allowLocalDependencies === true,
  allowOther:
    governance?.allowOther === true,
});

const normalizeDisplayDefaults = (displayDefaults = {}) => ({
  placeholder: normalizeString(displayDefaults?.placeholder, ''),
  helperText: normalizeString(displayDefaults?.helperText, ''),
  hint: normalizeString(displayDefaults?.hint, ''),
  otherHint: normalizeString(displayDefaults?.otherHint, 'Please specify if other'),
  otherHelperText: normalizeString(
    displayDefaults?.otherHelperText,
    'Provide more information for "Other"'
  ),
  otherErrorText: normalizeString(
    displayDefaults?.otherErrorText,
    'You must specify when selecting "Other"'
  ),
  otherMaxLength: Number.isFinite(Number(displayDefaults?.otherMaxLength))
    ? Number(displayDefaults.otherMaxLength)
    : 30,
  additionalTextHint: normalizeString(
    displayDefaults?.additionalTextHint,
    'Please provide additional information'
  ),
  additionalTextHelperText: normalizeString(
    displayDefaults?.additionalTextHelperText,
    'Enter further details if necessary'
  ),
  additionalTextErrorText: normalizeString(
    displayDefaults?.additionalTextErrorText,
    'Additional details required'
  ),
  additionalTextMaxLength: Number.isFinite(Number(displayDefaults?.additionalTextMaxLength))
    ? Number(displayDefaults.additionalTextMaxLength)
    : 30,
});

const resolveManagedFieldRuntimeType = (managedFieldDefinition) => {
  const sourceType = normalizeString(managedFieldDefinition?.type);

  // IMPORTANT:
  // - Today we are primarily introducing org-managed single-select / multi-select
  // - asset-type already exists in your codebase as a semantic single-select
  //   with downstream DAM meaning
  //
  // We keep asset-type distinct at the definition level, but rely on your
  // existing getDefaultFieldMetadata(...) normalization for its runtime shape.
  if (sourceType === 'asset-type') {
    return 'asset-type';
  }

  return sourceType;
};

const resolveManagedOptionSetId = (managedFieldDefinition) => {
  // Support both shapes:
  //  1) optionsSource.optionsSetId    (preferred)
  //  2) managedOptionSetId            (fallback)
  const fromOptionsSource = normalizeString(
    managedFieldDefinition?.optionsSource?.optionsSetId,
    ''
  );

  if (fromOptionsSource) return fromOptionsSource;

  return normalizeString(managedFieldDefinition?.managedOptionSetId, '');
};

const buildGovernedFieldLabel = (
  managedFieldDefinition,
  governance = {},
  explicitOverride = null
) => {
  // If governance allows local label override and caller supplies one, use it.
  // Otherwise preserve the centrally governed label.
  const override = normalizeString(explicitOverride, '');
  if (governance.allowLocalLabelOverride && override) {
    return override;
  }

  return normalizeString(managedFieldDefinition?.label, 'Managed field');
};

const buildGovernedRequiredFlag = (
  managedFieldDefinition,
  governance = {},
  explicitOverride = undefined
) => {
  // If governance allows local required override and caller provided a boolean,
  // use it. Otherwise preserve the managed definition's required state if present.
  if (
    governance.allowLocalRequiredOverride &&
    typeof explicitOverride === 'boolean'
  ) {
    return explicitOverride;
  }

  if (typeof managedFieldDefinition?.required === 'boolean') {
    return managedFieldDefinition.required;
  }

  return false;
};

const buildGovernedPlaceholder = (
  managedFieldDefinition,
  governance = {},
  explicitOverride = null
) => {
  const override = normalizeString(explicitOverride, '');
  if (governance.allowLocalPlaceholderOverride && override) {
    return override;
  }

  return normalizeString(
    managedFieldDefinition?.displayDefaults?.placeholder,
    ''
  );
};

const buildGovernedHelperText = (
  managedFieldDefinition,
  governance = {},
  explicitOverride = null
) => {
  const override = normalizeString(explicitOverride, '');
  if (governance.allowLocalHelperTextOverride && override) {
    return override;
  }

  return normalizeString(
    managedFieldDefinition?.displayDefaults?.helperText,
    ''
  );
};

const buildGovernedHint = (managedFieldDefinition) => {
  return normalizeString(
    managedFieldDefinition?.displayDefaults?.hint,
    ''
  );
};

const isDropdownRuntimeType = (runtimeType) => {
  const t = normalizeString(runtimeType);
  return t === 'single-select' || t === 'multi-select' || t === 'asset-type';
};

const isMultiSelectRuntimeType = (runtimeType) => {
  return normalizeString(runtimeType) === 'multi-select';
};

// -----------------------------------------------------------------------------
// Public helpers
// -----------------------------------------------------------------------------

/**
 * Returns a normalized "managed field instance blueprint" that callers can use
 * before building final metadata.
 *
 * This is helpful for logging, validation, and future policy checks.
 */
export const normalizeManagedFieldInstanceBlueprint = ({
  organizationId,
  managedFieldDefinition,
  explicitLabelOverride = null,
  explicitRequiredOverride = undefined,
  explicitPlaceholderOverride = null,
  explicitHelperTextOverride = null,
}) => {
  assertManagedFieldDefinition(managedFieldDefinition);

  const managedFieldId = normalizeString(managedFieldDefinition?.id);
  const managedFieldVersion = Number.isFinite(
    Number(managedFieldDefinition?.version)
  )
    ? Number(managedFieldDefinition.version)
    : 1;

  const runtimeType = resolveManagedFieldRuntimeType(managedFieldDefinition);
  const managedOptionSetId = resolveManagedOptionSetId(managedFieldDefinition);
  const governance = normalizeGovernance(
    managedFieldDefinition?.governance || {}
  );
  const displayDefaults = normalizeDisplayDefaults(
    managedFieldDefinition?.displayDefaults || {}
  );

  const blueprint = {
    organizationId: normalizeNullableString(organizationId),
    managedFieldId,
    managedFieldVersion,
    runtimeType,
    managedOptionSetId: normalizeNullableString(managedOptionSetId),
    semanticKey: normalizeNullableString(managedFieldDefinition?.semanticKey),
    assetFieldRole: normalizeNullableString(
      managedFieldDefinition?.assetFieldRole
    ),
    scope: MANAGED_FIELD_INSTANCE_SCOPE.FORM_INSTANCE,
    optionsSource: MANAGED_FIELD_INSTANCE_SOURCE.ORGANIZATION,
    governance,
    displayDefaults,
    resolvedLabel: buildGovernedFieldLabel(
      managedFieldDefinition,
      governance,
      explicitLabelOverride
    ),
    resolvedRequired: buildGovernedRequiredFlag(
      managedFieldDefinition,
      governance,
      explicitRequiredOverride
    ),
    resolvedPlaceholder: buildGovernedPlaceholder(
      managedFieldDefinition,
      governance,
      explicitPlaceholderOverride
    ),
    resolvedHelperText: buildGovernedHelperText(
      managedFieldDefinition,
      governance,
      explicitHelperTextOverride
    ),
    resolvedHint: buildGovernedHint(managedFieldDefinition),
  };

  return blueprint;
};

/**
 * Creates a fully-formed field metadata instance for insertion into a form.
 *
 * OUTPUT
 *  • fieldId              -> new local instance id
 *  • ufi                  -> new local answer identity
 *  • metadata             -> field metadata aligned with existing builder contract
 *  • layoutField          -> layout-safe object for immediate insertion into layoutConfig
 *
 * IMPORTANT
 *  • We intentionally reuse getDefaultFieldMetadata(...) to stay aligned with:
 *      - FieldMetadataContext
 *      - FormLayoutContext
 *      - FieldRenderer
 *      - MultiAnswerFieldContext
 *      - existing save/publish flows
 *
 * ALIGNMENT NOTE
 *  • getDefaultFieldMetadata(...) creates generic local dropdown defaults
 *    (including Option 1 / 2 / 3). That is correct for LOCAL fields but WRONG
 *    for ORG-MANAGED dropdowns.
 *  • Therefore, for managed dropdown fields we explicitly override the dropdown
 *    metadata section to preserve the runtime shape while preventing local
 *    placeholder options from becoming the source of truth.
 */
export const buildManagedFieldInstance = ({
  organizationId,
  managedFieldDefinition,

  // Optional explicit local instance ids (useful for deterministic tests or
  // future restore/import flows). If not supplied, we generate them.
  fieldId: explicitFieldId = null,
  ufi: explicitUfi = null,

  // Optional builder-side overrides. These are only applied if governance allows.
  labelOverride = null,
  requiredOverride = undefined,
  placeholderOverride = null,
  helperTextOverride = null,
}) => {
  assertManagedFieldDefinition(managedFieldDefinition);

  const blueprint = normalizeManagedFieldInstanceBlueprint({
    organizationId,
    managedFieldDefinition,
    explicitLabelOverride: labelOverride,
    explicitRequiredOverride: requiredOverride,
    explicitPlaceholderOverride: placeholderOverride,
    explicitHelperTextOverride: helperTextOverride,
  });

  const fieldId = normalizeString(explicitFieldId, uuidv4());
  const ufi = normalizeString(explicitUfi, uuidv4());

  // IMPORTANT:
  // We preserve a stable managedOptionSetId reference, but we still feed it into
  // optionsSetUFI because your current dropdown architecture already expects that.
  //
  // This is the compatibility bridge that lets us avoid breaking:
  //  • SingleSelectFieldComponent
  //  • MultiSelectFieldComponent
  //  • MultiAnswerFieldContext
  //
  // Later, the runtime option resolver can treat optionsSource === "organization"
  // as the authoritative indicator that the options come from org settings.
  const defaultMetadataMap = getDefaultFieldMetadata(
    fieldId,
    ufi,
    blueprint.runtimeType,
    blueprint.managedOptionSetId || null
  );

  const defaultMetadata = defaultMetadataMap?.[fieldId] || {};

  const managedInstanceMetadata = {
    ...defaultMetadata,

    // -------------------------------------------------------------------------
    // Local form-instance identity
    // -------------------------------------------------------------------------
    id: fieldId,
    ufi,
    type: defaultMetadata.type || blueprint.runtimeType,

    // -------------------------------------------------------------------------
    // Central managed-definition references
    // -------------------------------------------------------------------------
    orgManaged: true,
    organizationId: blueprint.organizationId,
    managedFieldId: blueprint.managedFieldId,
    managedFieldVersion: blueprint.managedFieldVersion,
    managedOptionSetId: blueprint.managedOptionSetId,
    optionsSource: blueprint.optionsSource,
    managedFieldScope: blueprint.scope,

    // -------------------------------------------------------------------------
    // Governance metadata
    // -------------------------------------------------------------------------
    governance: blueprint.governance,
    allowLocalLabelOverride: blueprint.governance.allowLocalLabelOverride,
    allowLocalRequiredOverride: blueprint.governance.allowLocalRequiredOverride,
    allowLocalHelperTextOverride:
      blueprint.governance.allowLocalHelperTextOverride,
    allowLocalPlaceholderOverride:
      blueprint.governance.allowLocalPlaceholderOverride,
    allowLocalOptionEditing: blueprint.governance.allowLocalOptionEditing,
    allowLocalDependencies: blueprint.governance.allowLocalDependencies,

    // -------------------------------------------------------------------------
    // Semantic/system identity
    // -------------------------------------------------------------------------
    semanticKey: blueprint.semanticKey,
    ...(blueprint.assetFieldRole
      ? { assetFieldRole: blueprint.assetFieldRole }
      : {}),

    // -------------------------------------------------------------------------
    // Governed field presentation
    // -------------------------------------------------------------------------
    label: blueprint.resolvedLabel,
    required: blueprint.resolvedRequired,
    placeholder:
      blueprint.resolvedPlaceholder || defaultMetadata.placeholder || '',
    helperText:
      blueprint.resolvedHelperText || defaultMetadata.helperText || '',
    hint:
      blueprint.resolvedHint || defaultMetadata.hint || '',

    // -------------------------------------------------------------------------
    // Dropdown compatibility bridge
    // -------------------------------------------------------------------------
    ...(blueprint.managedOptionSetId
      ? {
          optionsSetUFI: blueprint.managedOptionSetId,
        }
      : {}),

    // Allow "Other" only if governance permits it
    ...(blueprint.governance.allowOther
      ? { allowOther: true, includeOther: true }
      : { allowOther: false, includeOther: false }),
  };

  // ---------------------------------------------------------------------------
  // IMPORTANT: normalize org-managed dropdown metadata to the EXACT shape your
  // current runtime expects, while preventing local default options from leaking
  // into org-managed fields.
  // ---------------------------------------------------------------------------
  if (isDropdownRuntimeType(blueprint.runtimeType)) {
    const optionsSetUFI = blueprint.managedOptionSetId || managedInstanceMetadata.optionsSetUFI || null;
    const isMulti = isMultiSelectRuntimeType(blueprint.runtimeType);

    managedInstanceMetadata.optionsSetUFI = optionsSetUFI;
    managedInstanceMetadata.multiselect = isMulti;

    // CRITICAL:
    // Replace generic local defaults from getDefaultFieldMetadata(...) with an
    // EMPTY embedded option set. This preserves the runtime shape expected by:
    //   • MultiAnswerFieldContext
    //   • SingleSelectFieldComponent / MultiSelectFieldComponent
    // but prevents "Option 1 / 2 / 3" from appearing as the source of truth.
    managedInstanceMetadata.options = {
      optionsSetUFI,
      options: [],
    };

    // Preserve the exact fields your current dropdown metadata shape expects.
    managedInstanceMetadata.dynamicOptions = Array.isArray(
      defaultMetadata.dynamicOptions
    )
      ? defaultMetadata.dynamicOptions
      : [];

    managedInstanceMetadata.additionalText = normalizeString(
      defaultMetadata.additionalText,
      ''
    );

    managedInstanceMetadata.otherHint =
      blueprint.displayDefaults.otherHint ||
      defaultMetadata.otherHint ||
      'Please specify if other';

    managedInstanceMetadata.otherHelperText =
      blueprint.displayDefaults.otherHelperText ||
      defaultMetadata.otherHelperText ||
      'Provide more information for "Other"';

    managedInstanceMetadata.otherErrorText =
      blueprint.displayDefaults.otherErrorText ||
      defaultMetadata.otherErrorText ||
      'You must specify when selecting "Other"';

    managedInstanceMetadata.otherMaxLength =
      Number.isFinite(Number(blueprint.displayDefaults.otherMaxLength))
        ? Number(blueprint.displayDefaults.otherMaxLength)
        : (
            Number.isFinite(Number(defaultMetadata.otherMaxLength))
              ? Number(defaultMetadata.otherMaxLength)
              : 30
          );

    managedInstanceMetadata.additionalTextHint =
      blueprint.displayDefaults.additionalTextHint ||
      defaultMetadata.additionalTextHint ||
      'Please provide additional information';

    managedInstanceMetadata.additionalTextHelperText =
      blueprint.displayDefaults.additionalTextHelperText ||
      defaultMetadata.additionalTextHelperText ||
      'Enter further details if necessary';

    managedInstanceMetadata.additionalTextErrorText =
      blueprint.displayDefaults.additionalTextErrorText ||
      defaultMetadata.additionalTextErrorText ||
      'Additional details required';

    managedInstanceMetadata.additionalTextMaxLength =
      Number.isFinite(Number(blueprint.displayDefaults.additionalTextMaxLength))
        ? Number(blueprint.displayDefaults.additionalTextMaxLength)
        : (
            Number.isFinite(Number(defaultMetadata.additionalTextMaxLength))
              ? Number(defaultMetadata.additionalTextMaxLength)
              : 30
          );

    managedInstanceMetadata.dependencies = isObject(defaultMetadata.dependencies)
      ? defaultMetadata.dependencies
      : { triggerOptions: {} };

    if (!isObject(managedInstanceMetadata.dependencies)) {
      managedInstanceMetadata.dependencies = { triggerOptions: {} };
    }

    if (!isObject(managedInstanceMetadata.dependencies.triggerOptions)) {
      managedInstanceMetadata.dependencies.triggerOptions = {};
    }

    // IMPORTANT:
    // Multi-select defaultValue should remain an array; single-select remains ''.
    managedInstanceMetadata.defaultValue = isMulti ? [] : '';

    // IMPORTANT:
    // maxLength is not meaningful for dropdowns in your current system.
    managedInstanceMetadata.maxLength = null;
  }

  // IMPORTANT:
  // The layout object should remain lightweight and compatible with your current
  // serializeLayoutConfig(...) expectations. We include only what the layout
  // needs for reconstruction and builder UX.
  const layoutField = {
    id: fieldId,
    ufi,
    type: managedInstanceMetadata.type,
    label: managedInstanceMetadata.label,
    placeholder: managedInstanceMetadata.placeholder,
    required: managedInstanceMetadata.required,
    index: 0,
    ...(managedInstanceMetadata.optionsSetUFI
      ? { optionsSetUFI: managedInstanceMetadata.optionsSetUFI }
      : {}),
  };

  // Header fields are not in current phase-1 scope, but keep parity if this
  // utility is later expanded.
  if (layoutField.type === 'header') {
    layoutField.headerStyle = managedInstanceMetadata.headerStyle || undefined;
    layoutField.subheaderStyle =
      managedInstanceMetadata.subheaderStyle || undefined;
    delete layoutField.style;
  }

  console.log('[managedFieldInstances] Built managed field instance successfully:', {
    organizationId: blueprint.organizationId,
    managedFieldId: blueprint.managedFieldId,
    fieldId,
    ufi,
    type: managedInstanceMetadata.type,
    managedOptionSetId: blueprint.managedOptionSetId,
    optionsSetUFI: managedInstanceMetadata.optionsSetUFI || null,
    semanticKey: blueprint.semanticKey,
    assetFieldRole: blueprint.assetFieldRole || null,
    allowOther: managedInstanceMetadata.allowOther === true,
    multiselect: managedInstanceMetadata.multiselect === true,
  });

  return {
    fieldId,
    ufi,
    metadata: managedInstanceMetadata,
    layoutField,
    blueprint,
  };
};

/**
 * Returns true when a field metadata object represents an org-managed field instance.
 *
 * This utility is intentionally lightweight so contexts/components can use it
 * safely without importing broader domain logic.
 */
export const isManagedFieldInstance = (fieldMetadata) => {
  return (
    !!fieldMetadata &&
    typeof fieldMetadata === 'object' &&
    fieldMetadata.orgManaged === true &&
    !!normalizeString(fieldMetadata?.managedFieldId, '')
  );
};

/**
 * Returns true when a managed field instance is backed by an org-managed option set.
 *
 * Use this later in dropdown option resolution so form-local dropdowns and
 * org-managed dropdowns can share the same rendering pipeline safely.
 */
export const isOrgManagedDropdownInstance = (fieldMetadata) => {
  return (
    isManagedFieldInstance(fieldMetadata) &&
    (
      normalizeString(fieldMetadata?.type) === 'single-select' ||
      normalizeString(fieldMetadata?.type) === 'multi-select'
    ) &&
    normalizeString(fieldMetadata?.optionsSource) ===
      MANAGED_FIELD_INSTANCE_SOURCE.ORGANIZATION &&
    !!normalizeString(fieldMetadata?.managedOptionSetId, '')
  );
};

/**
 * Converts a managed field instance into a compact builder-palette payload.
 *
 * This is not yet used in the UI, but defining it here now keeps the contract
 * centralized before we wire the frontend.
 */
export const buildManagedFieldPreviewDescriptor = ({
  managedFieldDefinition,
}) => {
  assertManagedFieldDefinition(managedFieldDefinition);

  const runtimeType = resolveManagedFieldRuntimeType(managedFieldDefinition);

  const descriptor = {
    isManagedFieldPreview: true,
    managedFieldId: normalizeString(managedFieldDefinition?.id),
    label: normalizeString(managedFieldDefinition?.label, 'Managed field'),
    type: runtimeType,
    semanticKey: normalizeNullableString(managedFieldDefinition?.semanticKey),
    assetFieldRole: normalizeNullableString(
      managedFieldDefinition?.assetFieldRole
    ),
    managedOptionSetId: normalizeNullableString(
      resolveManagedOptionSetId(managedFieldDefinition)
    ),
  };

  return descriptor;
};